import React from "react";
import { TAnswers, ISurvey, ISurveySection } from "../surveyTypes";
import CircularProgress from "@material-ui/core/CircularProgress";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import AgreementDialog from "./AgreementDialog";
import { makeStyles, Typography } from "@material-ui/core";
import SurveySection from "./SurveySection";
import axios from "axios";

const useStyles = makeStyles((theme) => ({
  questionGroup: {
    margin: theme.spacing(2, 0, 0, 0),
  },
  submitGroup: {
    margin: theme.spacing(2, 0, 2, 0),
  },
  submitHint: {
    color: "red",
  },
}));

interface SurveyProps {
  invitationId: string;
  initialAnswers: TAnswers;
  survey: ISurvey;
  setInvitationStatus: (invitationStatus: number) => void;
}

type SurveyFormContextType = {
  answers: TAnswers;
  setAnswerValue: (questionId: number, value: number) => void;
  setAnswerComment: (questionId: number, comment: string) => void;
};

export const SurveyFormContext = React.createContext<SurveyFormContextType>({
  answers: {},
  setAnswerValue: (questionId: number, value: number) => {},
  setAnswerComment: (questionId: number, comment: string) => {},
});

const Survey: React.FC<SurveyProps> = (props: SurveyProps) => {
  const baseURL = process.env.REACT_APP_VTSURVEY_BASE_URL || "";
  const { invitationId, initialAnswers, survey, setInvitationStatus } = props;
  const [loading, setLoading] = React.useState(false);
  const [answers, setAnswers] = React.useState<TAnswers>({});
  const [formComplete, setFormComplete] = React.useState(false);
  const [statusCode, setStatusCode] = React.useState();
  const [responseMessage, setResponseMessage] = React.useState();
  const [agreed, setAgreed] = React.useState(false);
  const classes = useStyles();
  const postAnswers = async (baseURL: string, invitationId: string) => {
    const path = `/api/campaigns/invitation/${invitationId}/respond`;
    const fullURL = baseURL + path;
    setLoading(true);
    let response = null;
    try {
      const res = await axios.post(fullURL, answers);
      response = res;
    } catch (error) {
      response = error.response;
    } finally {
      setLoading(false);
    }
    return response;
  };

  const handleSubmit = async () => {
    const response = await postAnswers(baseURL, invitationId);
    if (response.status === 201) {
      201 && setInvitationStatus(2);
    } else {
      setStatusCode(response.status);
      setResponseMessage(response.data);
    }
  };

  React.useEffect(() => {
    setAnswers(initialAnswers);
  }, [initialAnswers]);

  React.useEffect(() => {
    let newFormComplete = true;
    const answerArray = Object.values(answers);
    for (var answer of answerArray) {
      if (answer.value === null) {
        newFormComplete = false;
        break;
      }
    }
    setFormComplete(newFormComplete);
  }, [answers]);

  const SurveyFormContextValue = {
    answers: answers,
    setAnswerValue: (questionId: number, value: number) => {
      if (questionId in answers) {
        let newAnswers: TAnswers = {};
        Object.assign(newAnswers, answers);
        newAnswers[questionId].value = value;
        setAnswers(newAnswers);
      }
    },
    setAnswerComment: (questionId: number, comment: string) => {
      if (questionId in answers) {
        let newAnswers: TAnswers = {};
        Object.assign(newAnswers, answers);
        newAnswers[questionId].comment = comment;
        setAnswers(newAnswers);
      }
    },
  };

  return (
    <React.Fragment>
      <AgreementDialog open={!agreed} setAgreed={setAgreed} />
      <Grid container direction="column" spacing={2}>
        <SurveyFormContext.Provider value={SurveyFormContextValue}>
          {Object.values(survey).map(
            (section: ISurveySection, index: number) => (
              <Grid item key={index}>
                <SurveySection {...section} />
              </Grid>
            )
          )}
        </SurveyFormContext.Provider>
        <br />
        <Grid
          container
          item
          direction="row"
          justify="flex-end"
          alignItems="center"
          spacing={3}
          className={classes.submitGroup}
        >
          {!formComplete && (
            <Grid item>
              <Typography className={classes.submitHint} variant="caption">
                Please complete all questions
              </Typography>
            </Grid>
          )}
          <Grid item>
            {loading && <CircularProgress />}
            {!loading && (
              <Button
                variant="contained"
                color="primary"
                disabled={!formComplete}
                onClick={handleSubmit}
              >
                Submit
              </Button>
            )}
            {!loading && statusCode && (
              <React.Fragment>
                <Typography>{statusCode}</Typography>
                <Typography>{responseMessage}</Typography>
              </React.Fragment>
            )}
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export default Survey;
